// src/pages/AddItemCategory.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import EditItemCategoryModal from "../components/addItemEditModal";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import InventoryNav from "../components/inventoryNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { useNavigate, Link } from "react-router-dom";
import BulkItemCategoryUpload from "../components/bulkItemCategoryUpload";

const API = process.env.REACT_APP_API_BASE || "https://rfs2.infraconsoft.net.in/api";

const AddItemCategory = () => {
  useAuthRedirect();
  const branch = localStorage.getItem("branch");
  const userId = localStorage.getItem("username");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ newPurNo: "", catName: "" });
  const [stnNo, setStnNo] = useState("");

  // streamed dataset
  const [data, setData] = useState([]);
  const [menuByCode, setMenuByCode] = useState({});
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);

  // search (server-side)
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceRef = useRef(null);
  const cancelTokenRef = useRef(null);
  const streamCancelRef = useRef({ cancelled: false });

  // client pager
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useMemo(
    () => localStorage.getItem("permissions") || localStorage.getItem("userPermissions"),
    []
  );
  const canView  = hasPermission("Item Code Generation", "view");
  const canEntry = hasPermission("Item Code Generation", "entry");
  const canEdit  = hasPermission("Item Code Generation", "edit");
  const canDel   = hasPermission("Item Code Generation", "del");

  const [bulkOpen, setBulkOpen] = useState(false);

  // ---- initial category code
  useEffect(() => {
    if (!canEntry) return;
    (async function fetchStnNo() {
      try {
        Swal.fire({
          title: "Loading Category Code",
          html: `
            <div style="width:100%;background:#e0e0e0;border-radius:5px;">
              <div style="height:10px;width:0%;background:linear-gradient(to right,#4facfe,#00f2fe);animation:progressBar 2s linear infinite;"></div>
            </div>
            <p style="margin-top:10px;font-size:14px;">Please wait while we fetch Category Code...</p>
          `,
          showConfirmButton: false,
          allowOutsideClick: false,
          didOpen: () => {
            const style = document.createElement("style");
            style.innerHTML = `@keyframes progressBar{0%{width:0%}50%{width:100%}100%{width:0%}}`;
            document.head.appendChild(style);
          },
        });

        const response = await axios.get(`${API}/itemCat_no`);
        if (response.data.newCatCode) {
          setStnNo(response.data.newCatCode);
          setFormData((prev) => ({ ...prev, newPurNo: response.data.newCatCode }));
        }
      } catch (error) {
        console.error("Error fetching Cateogory Code:", error);
        Swal.fire({ icon: "error", title: "Oops!", text: "Error fetching Category Code. Please try again" });
      } finally {
        Swal.close();
      }
    })();
  }, [canEntry]);

  // ---- streaming loader: 100 rows per second using server-side cursor
  const startStream = async (term = "") => {
    // cancel previous
    streamCancelRef.current.cancelled = true;
    if (cancelTokenRef.current) cancelTokenRef.current.cancel("New stream");
    // new run
    streamCancelRef.current = { cancelled: false };
    const thisRun = streamCancelRef.current;

    setLoading(true);
    setStreaming(true);
    setData([]);
    setMenuByCode({});
    setCurrentPage(1);

    let after = null;
    const limit = 100;

    while (!thisRun.cancelled) {
      try {
        cancelTokenRef.current = axios.CancelToken.source();
        const { data: resp } = await axios.get(`${API}/itemCat_list`, {
          params: { limit, after, search: term || undefined },
          cancelToken: cancelTokenRef.current.token,
        });

        setData((prev) => [...prev, ...(resp.itemCatList || [])]);
        setMenuByCode((prev) => ({ ...prev, ...(resp.menuByCode || {}) }));

        if (!resp.nextCursor) break;
        after = resp.nextCursor;

        // throttle: ~1 request per second
        await new Promise((r) => setTimeout(r, 1000));
      } catch (err) {
        if (axios.isCancel(err)) return; // stopped by new search/navigation
        console.error("Stream load error:", err);
        break;
      }
    }

    if (!thisRun.cancelled) {
      setStreaming(false);
      setLoading(false);
    }
  };

  // initial load (no search)
  useEffect(() => { if (canView) startStream(""); }, [canView]);

  // search with debounce (server-side; streams all matches)
  const onSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => startStream(term.trim()), 300);
  };

  // form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const fetchNewCategoryNumber = async () => {
    try {
      const response = await axios.get(`${API}/itemCat_no`);
      if (response.data.newCatCode) {
        setStnNo(response.data.newCatCode);
        setFormData((prev) => ({ ...prev, newPurNo: response.data.newCatCode }));
      }
    } catch (error) {
      console.error("Error fetching category number:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.catName.trim()) {
      Swal.fire({ icon: "warning", title: "Empty Field", text: "Please enter a valid category name!" });
      return;
    }
    try {
      Swal.fire({ title: "Submitting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const response = await axios.post(`${API}/itemCat_insert`, {
        branch,
        user: userId,
        code: formData.newPurNo,
        name: formData.catName,
      });
      if (response.data.success) {
        Swal.fire({ icon: "success", title: "Success", text: "Item Category added successfully!" });
        await fetchNewCategoryNumber();
        setFormData({ newPurNo: stnNo, catName: "" });
        // refresh from beginning to include the new one
        startStream(searchTerm.trim());
      } else {
        Swal.fire({ icon: "error", title: "Error", text: response.data.message || "Submission failed." });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.message || "Submission failed." });
    }
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Swal.fire({ title: "Deleting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
          await axios.delete(`${API}/itemCat_delete/${id}`);
          Swal.fire({ title: "Deleted!", text: "Your record has been deleted.", icon: "success" });
          // remove locally to avoid full reload
          setData((prev) => prev.filter((x) => x.id !== id));
        } catch (error) {
          console.error("Error deleting category:", error);
          Swal.fire({ title: "Error!", text: "Failed to delete category.", icon: "error" });
        }
      }
    });
  };

  // Edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ id: "", catName: "" });
  const openEditModal = (item) => {
    setEditFormData({ id: item.id, catNo: item.code, catName: item.itemName });
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => setIsEditModalOpen(false);

  // After bulk commit, refresh from start
  const refreshAfterCommit = async () => {
    await fetchNewCategoryNumber();
    startStream(searchTerm.trim());
  };

  // client-side pagination over the growing dataset
  const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));
  const clampedPage = Math.min(currentPage, totalPages);
  const indexOfLastItem = clampedPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = data.slice(indexOfFirstItem, indexOfLastItem);

  if (!canView) {
    return (
      <div id="admin-container">
        <InventoryNav />
        <div className="content-right" style={{ padding: 20 }}><Access /></div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <InventoryNav />
      <div className="content-right">
        <section>
          <h2 className="add-txt">Item Category</h2>
          <Time />
        </section>

        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          {canEntry && (
            <button className="submit-btn" type="button" onClick={() => setBulkOpen(true)}>
              CSV Upload
            </button>
          )}
          
        </div>

        <div className="form-container">
          {canEntry && (
            <form className="vehicle-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Category Code :</label>
                  <input type="text" name="newPurNo" value={formData.newPurNo} onChange={handleChange} readOnly />
                </div>
                <div className="form-group">
                  <label>Name :</label>
                  <input type="text" name="catName" value={formData.catName} onChange={handleChange} />
                </div>
              </div>
              <div className="btn-cls">
                <button type="submit" className="submit-btn">Save</button>
              </div>
            </form>
          )}

          {/* Search (server-side) */}
          <div className="search-filter">
            <p>Search: </p>
            <input
              type="text"
              placeholder="Search by Category Listing.."
              value={searchTerm}
              onChange={onSearchChange}
              className="search-input"
            />
          </div>

          {/* Listing Table */}
          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Category Listing</th>
                  {canEdit && <th>Edit</th>}
                  <th>Sub Category</th>
                  {canDel && <th>Delete</th>}
                </tr>
              </thead>
              <tbody>
                {loading && data.length === 0 ? (
                  <tr><td colSpan="6" className="loading-text">Loading…</td></tr>
                ) : currentData.length > 0 ? (
                  currentData.map((item, idx) => (
                    <tr key={item.id ?? `${item.code}-${idx}`}>
                      <td>{indexOfFirstItem + idx + 1}</td>
                      <td>{item.code} - ({item.itemName})</td>

                      {canEdit && (
                        <td>
                          <button
                            style={{ backgroundColor: "transparent", border: "none", color: "blue", cursor: "pointer" }}
                            title="Edit"
                            onClick={() => openEditModal(item)}
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                        </td>
                      )}

                      <td>
                        <Link to={`/inventory/add_Item_Subcategory/${item.code}/${item.itemName}`}>Click</Link>
                      </td>

                      {canDel && (
                        <td className="del">
                          {menuByCode[item.code] && menuByCode[item.code].length > 0 ? (
                            "No Access"
                          ) : (
                            <i className="fa-solid fa-trash-can" onClick={() => handleDelete(item.id)}></i>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan="6">No Data Available</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={clampedPage === 1}>
              Previous
            </button>

            <button onClick={() => setCurrentPage(1)} className={clampedPage === 1 ? "active" : ""}>
              1
            </button>

            {[...Array(Math.min(3, totalPages - 1))].map((_, i) => {
              const pageNumber = Math.min(totalPages, clampedPage + i);
              if (pageNumber > 1) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={clampedPage === pageNumber ? "active" : ""}
                  >
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={clampedPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <EditItemCategoryModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        fetchData={() => startStream(searchTerm.trim())}
      />

      {/* Bulk Upload Modal */}
      <BulkItemCategoryUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onCommitted={refreshAfterCommit}
      />
    </div>
  );
};

export default AddItemCategory;
